import { MutableSetStore } from '@deqo/foundation';

export type PermissionCode =
    'base:read'             // Base: Read
    | 'base:write'          // Base: Write
    | 'ac_billing:read'     // Admin/Billing: Read
    | 'ac_billing:write'    // Admin/Billing: Write
    | 'ac_team:read'        // Admin/Team: Read
    | 'ac_team:write'       // Admin/Team: Write
    | 'bo:read'             // Board: Read
    | 'bo:write'            // Board: Write
    | 'bo:facilitate'       // Board: Facilitate
    | 'bo:knock'            // Board: Knock
    | 'da:read'             // Dashboard: Read
    | 'da:write'            // Dashboard: Read
    | 'wo:read'             // Workspace: Read
    | 'wo:write'            // Workspace: Write
    ;

const dependencies = new Map<PermissionCode, PermissionCode[]>([
    ['base:read', [

    ]],
    ['base:write', [
        'base:read'
    ]],
    ['ac_billing:read', [
        'base:read'
    ]],
    ['ac_billing:write', [
        'base:write',
        'ac_billing:read'
    ]],
    ['ac_team:read', [
        'base:read'
    ]],
    ['ac_team:write', [
        'base:write',
        'ac_team:read'
    ]],
    ['bo:read', [
        'base:read'
    ]],
    ['bo:write', [
        'base:write',
        'bo:read'
    ]],
    ['bo:facilitate', [
        'bo:write'
    ]],
    ['bo:knock', [
        'base:read'
    ]],
    ['da:read', [
        'base:read'
    ]],
    ['da:write', [
        'base:write',
        'da:read'
    ]],
    ['wo:read', [
        'base:read'
    ]],
    ['wo:write', [
        'base:write',
        'wo:read'
    ]],
]);

export class PermissionSet extends Set<PermissionCode> {

    public verify(...codes: PermissionCode[]) {
        for (const c of codes) {
            if (!this.has(c)) return false;
            if (!this.verify(...(dependencies.get(c) ?? []))) return false;
        }
        return true;
    }

    public toArray() {
        return Array.from(this);
    }
}

export class PassportModel extends MutableSetStore<PermissionCode> {

    public static create(...sources: Iterable<string>[]) {
        const merged = sources.map(x => Array.from(x)).reduce((t, x) => t.concat(x)) as any[];
        return new PassportModel(new Set(merged)) as PassportModel;
    }

    public verify(...codes: PermissionCode[]) {
        return PermissionSet.prototype.verify.apply(this, codes);
    }
}

export interface UserRole {
    name: string;
    label: string;
    level: number;
}

const all = Object.freeze([
    {
        name: 'account_owner',
        label: 'Owner',
        badge: 'Owner',
        desc: 'The highest level of control.  This role includes all Admin functionality and additionally billing management.',
        level: 0
    },
    {
        name: 'account_admin',
        label: 'Admin',
        badge: 'Admin',
        desc: 'Admins have access to most management screens and can handle access requests, manage workspaces and users.',
        level: 1
    },
    {
        name: 'account_member',
        label: 'Member',
        badge: 'Member',
        desc: 'The default role for most people.  Members can particpate in meetings and create/update boards and workspaces.',
        level: 2
    },
    {
        name: 'account_guest',
        label: 'Guest',
        badge: 'Guest',
        desc: 'A restricted role to allow only basic access to the account.  Guests are free and do not affect billing.',
        level: 3
    },
]);

const index = Object.fromEntries(all.map(x => [x.name, x]));

export const UserRole = {
    all,
    lookup: (roleName: string) => index[roleName],
    options: (passport: PassportModel, current?: string) => {
        if (passport.verify('ac_billing:write')) {
            return all.filter(x => x.name !== 'current');
        }
        if (passport.verify('ac_team:write') && current !== 'account_owner') {
            return all.filter(x => x.level >= 1 && x.name !== current);
        }
        return [];
    }
};
